import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { LAUNCH_CATEGORIES } from "./SmallTabs";
import ConsentDashboardTile from "./ConsentDashboardTile";
import { ActiveTriggersWidget, TriggerThread } from "./TriggerSystem";
import { FounderFireBadge, FieldNotesPanel } from "./FounderFire";
import OperatorManual from "./OperatorManual";
import jsPDF from "jspdf";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ===== Dashboard Home / Command Center =====
const OnboardingModal = ({ onClose, onSuccess }) => {
  const [form, setForm] = useState({ client_name: "", client_email: "", client_phone: "", property_address: "", city: "DFW", unit_number: "", property_type: "apartment", monthly_rent: "", notes: "", send_welcome_email: true });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/onboard-client`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ ...form, monthly_rent: parseFloat(form.monthly_rent) || 0 })
      });
      if (r.ok) {
        const data = await r.json();
        setResult(data);
        if (onSuccess) onSuccess();
      }
    } catch (err) { console.error("Onboarding error:", err); }
    setSubmitting(false);
  };

  if (result) {
    return (
      <div className="onboard-overlay" onClick={onClose}>
        <div className="onboard-modal onboard-success" onClick={e => e.stopPropagation()} data-testid="onboard-success">
          <h2>Client Onboarded</h2>
          <div className="onboard-success-details">
            <p><strong>{result.client_name}</strong></p>
            <p>{result.project?.address}{result.project?.unit_number ? ` — Unit ${result.project.unit_number}` : ""}</p>
            <p className="onboard-success-sub">Project ID: {result.project?.project_id}</p>
            {result.email_sent && <p className="onboard-email-sent">Welcome email sent</p>}
          </div>
          <button onClick={onClose} className="onboard-done-btn" data-testid="onboard-done-btn">Done</button>
        </div>
      </div>
    );
  }

  return (
    <div className="onboard-overlay" onClick={onClose}>
      <div className="onboard-modal" onClick={e => e.stopPropagation()} data-testid="onboard-modal">
        <h2>Onboard New Client</h2>
        <p className="onboard-desc">Creates client account, project, and sends welcome email — all in one step.</p>
        <form onSubmit={handleSubmit} className="onboard-form">
          <div className="onboard-row">
            <div className="onboard-field">
              <label>Client Name *</label>
              <input type="text" required value={form.client_name} onChange={e => setForm({ ...form, client_name: e.target.value })} placeholder="John Smith" data-testid="onboard-name" />
            </div>
            <div className="onboard-field">
              <label>Client Email *</label>
              <input type="email" required value={form.client_email} onChange={e => setForm({ ...form, client_email: e.target.value })} placeholder="john@email.com" data-testid="onboard-email" />
            </div>
          </div>
          <div className="onboard-row">
            <div className="onboard-field">
              <label>Phone</label>
              <input type="tel" value={form.client_phone} onChange={e => setForm({ ...form, client_phone: e.target.value })} placeholder="(555) 123-4567" data-testid="onboard-phone" />
            </div>
            <div className="onboard-field">
              <label>Property Type</label>
              <select value={form.property_type} onChange={e => setForm({ ...form, property_type: e.target.value })} data-testid="onboard-type">
                <option value="apartment">Apartment</option>
                <option value="single_family">Single Family</option>
                <option value="townhome">Townhome</option>
                <option value="duplex">Duplex</option>
                <option value="condo">Condo</option>
              </select>
            </div>
          </div>
          <div className="onboard-row">
            <div className="onboard-field onboard-field-wide">
              <label>Property Address *</label>
              <input type="text" required value={form.property_address} onChange={e => setForm({ ...form, property_address: e.target.value })} placeholder="1234 Main St, Arlington" data-testid="onboard-address" />
            </div>
            <div className="onboard-field">
              <label>Unit #</label>
              <input type="text" value={form.unit_number} onChange={e => setForm({ ...form, unit_number: e.target.value })} placeholder="204" data-testid="onboard-unit" />
            </div>
          </div>
          <div className="onboard-row">
            <div className="onboard-field">
              <label>City</label>
              <input type="text" value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} data-testid="onboard-city" />
            </div>
            <div className="onboard-field">
              <label>Monthly Rent</label>
              <input type="number" value={form.monthly_rent} onChange={e => setForm({ ...form, monthly_rent: e.target.value })} placeholder="1200" data-testid="onboard-rent" />
            </div>
          </div>
          <div className="onboard-field">
            <label>Notes</label>
            <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Any details about the scope, timeline, or special requests" rows="3" data-testid="onboard-notes"></textarea>
          </div>
          <label className="onboard-check">
            <input type="checkbox" checked={form.send_welcome_email} onChange={e => setForm({ ...form, send_welcome_email: e.target.checked })} data-testid="onboard-send-email" />
            <span>Send welcome email to client</span>
          </label>
          <div className="onboard-actions">
            <button type="button" onClick={onClose} className="onboard-cancel" data-testid="onboard-cancel">Cancel</button>
            <button type="submit" disabled={submitting} className="onboard-submit" data-testid="onboard-submit">
              {submitting ? "Creating..." : "Onboard Client"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ===== Shared Items (CEO ↔ COO) =====
const SharedItems = ({ dashboardRole }) => {
  const [items, setItems] = useState([]);
  const [sentItems, setSentItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ item_type: "note", title: "", content: "" });
  const [sending, setSending] = useState(false);

  const loadItems = useCallback(async () => {
    const [inR, outR] = await Promise.all([
      fetch(`${BACKEND_URL}/api/admin/shared-with-me`, { credentials: "include" }),
      fetch(`${BACKEND_URL}/api/admin/shared-by-me`, { credentials: "include" }),
    ]);
    if (inR.ok) setItems(await inR.json());
    if (outR.ok) setSentItems(await outR.json());
  }, []);

  useEffect(() => { loadItems(); }, [loadItems]);

  const sendItem = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    setSending(true);
    await fetch(`${BACKEND_URL}/api/admin/share`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(form)
    });
    setForm({ item_type: "note", title: "", content: "" });
    setShowForm(false);
    setSending(false);
    loadItems();
  };

  const markRead = async (itemId) => {
    await fetch(`${BACKEND_URL}/api/admin/shared/${itemId}/read`, { method: "PUT", credentials: "include" });
    setItems(prev => prev.map(i => i.item_id === itemId ? { ...i, read: true } : i));
  };

  const toLabel = dashboardRole === "ceo" ? "COO" : "CEO";
  const unreadCount = items.filter(i => !i.read).length;

  return (
    <div className="shared-items-section" data-testid="shared-items">
      <div className="shared-header">
        <h3>Shared Board {unreadCount > 0 && <span className="shared-badge">{unreadCount} new</span>}</h3>
        <button onClick={() => setShowForm(!showForm)} className="shared-send-btn" data-testid="share-new-btn">
          {showForm ? "Cancel" : `Share with ${toLabel}`}
        </button>
      </div>

      {showForm && (
        <form onSubmit={sendItem} className="shared-form" data-testid="share-form">
          <select value={form.item_type} onChange={e => setForm(f => ({ ...f, item_type: e.target.value }))} className="shared-type-select">
            <option value="note">Note</option>
            <option value="idea">Idea</option>
            <option value="contract">Contract Reference</option>
            <option value="color">Color / Design</option>
            <option value="task">Action Item</option>
          </select>
          <input type="text" placeholder="Title" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} className="shared-input" required data-testid="share-title-input" />
          <textarea placeholder="Details (optional)" value={form.content} onChange={e => setForm(f => ({ ...f, content: e.target.value }))} className="shared-textarea" rows={3} data-testid="share-content-input" />
          <button type="submit" disabled={sending} className="shared-submit-btn" data-testid="share-submit-btn">{sending ? "Sending..." : `Send to ${toLabel}`}</button>
        </form>
      )}

      {items.length > 0 && (
        <div className="shared-inbox">
          <h4>From {toLabel === "COO" ? "COO" : "CEO"}</h4>
          {items.slice(0, 10).map(item => (
            <div key={item.item_id} className={`shared-card ${!item.read ? "shared-card-unread" : ""}`} onClick={() => !item.read && markRead(item.item_id)} data-testid={`shared-item-${item.item_id}`}>
              <div className="shared-card-top">
                <span className={`shared-type-badge shared-type-${item.item_type}`}>{item.item_type}</span>
                <span className="shared-card-from">{item.from_name}</span>
                <span className="shared-card-date">{new Date(item.created_at).toLocaleDateString()}</span>
                {!item.read && <span className="shared-new-dot"></span>}
              </div>
              <strong className="shared-card-title">{item.title}</strong>
              {item.content && <p className="shared-card-content">{item.content}</p>}
            </div>
          ))}
        </div>
      )}

      {sentItems.length > 0 && (
        <div className="shared-sent">
          <h4>Sent to {toLabel}</h4>
          {sentItems.slice(0, 5).map(item => (
            <div key={item.item_id} className="shared-card shared-card-sent">
              <div className="shared-card-top">
                <span className={`shared-type-badge shared-type-${item.item_type}`}>{item.item_type}</span>
                <span className="shared-card-date">{new Date(item.created_at).toLocaleDateString()}</span>
                <span className={`shared-read-status ${item.read ? "shared-read" : "shared-unread"}`}>{item.read ? "Read" : "Unread"}</span>
              </div>
              <strong className="shared-card-title">{item.title}</strong>
            </div>
          ))}
        </div>
      )}

      {items.length === 0 && sentItems.length === 0 && !showForm && (
        <p className="shared-empty">No shared items yet. Use this board to send notes, ideas, contracts, and action items between CEO and COO.</p>
      )}
    </div>
  );
};

// ===== Certifications Tracker =====
const CertificationsTracker = ({ dashboardRole }) => {
  const [certs, setCerts] = useState([]);
  const [myChecks, setMyChecks] = useState({});
  const [partnerChecks, setPartnerChecks] = useState({});
  const [partnerName, setPartnerName] = useState("");
  const [loading, setLoading] = useState(true);

  const loadCerts = useCallback(async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/certifications`, { credentials: "include" });
      if (r.ok) {
        const data = await r.json();
        setCerts(data.certifications);
        setMyChecks(data.my_checks);
        setPartnerChecks(data.partner_checks);
        setPartnerName(data.partner_name);
      }
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadCerts(); }, [loadCerts]);

  const toggle = async (certId) => {
    setMyChecks(prev => ({ ...prev, [certId]: !prev[certId] }));
    await fetch(`${BACKEND_URL}/api/admin/certifications/${certId}/toggle`, { method: "PUT", credentials: "include" });
  };

  if (loading) return null;

  const categories = [...new Set(certs.map(c => c.category))];
  const myCount = Object.values(myChecks).filter(Boolean).length;
  const partnerCount = Object.values(partnerChecks).filter(Boolean).length;

  return (
    <div className="certs-section" data-testid="certifications-tracker">
      <div className="certs-header">
        <h3>Certifications & Milestones</h3>
        <div className="certs-summary">
          <span className="certs-count" data-testid="certs-my-count">You: {myCount}/{certs.length}</span>
          {partnerName && <span className="certs-count certs-partner-count" data-testid="certs-partner-count">{partnerName}: {partnerCount}/{certs.length}</span>}
        </div>
      </div>
      {categories.map(cat => (
        <div key={cat} className="certs-category">
          <h4 className="certs-cat-title">{cat}</h4>
          {certs.filter(c => c.category === cat).map(cert => (
            <label key={cert.cert_id} className={`certs-item ${myChecks[cert.cert_id] ? "certs-item-done" : ""}`} data-testid={`cert-${cert.cert_id}`}>
              <input type="checkbox" checked={!!myChecks[cert.cert_id]} onChange={() => toggle(cert.cert_id)} />
              <div className="certs-item-info">
                <span className="certs-item-name">{cert.name}</span>
                <span className="certs-item-desc">{cert.description}</span>
              </div>
              {partnerName && (
                <span className={`certs-partner-status ${partnerChecks[cert.cert_id] ? "certs-partner-done" : ""}`} title={`${partnerName}: ${partnerChecks[cert.cert_id] ? "Done" : "Not yet"}`}>
                  {partnerChecks[cert.cert_id] ? "+" : "—"}
                </span>
              )}
            </label>
          ))}
        </div>
      ))}
    </div>
  );
};

// ===== Founder Cue Widget =====
// "The system runs itself, queues you for moments that need your judgment."
const FounderCueWidget = ({ setTab, dashboardRole }) => {
  const [cues, setCues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dismissing, setDismissing] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchCues = async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/admin/founder-cues`, { credentials: "include" });
        if (r.ok) {
          const data = await r.json();
          if (!cancelled) setCues(Array.isArray(data) ? data : []);
        }
      } catch {}
      if (!cancelled) setLoading(false);
    };
    fetchCues();
    const interval = setInterval(fetchCues, 180000);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  const dismissCue = async (cueId) => {
    setDismissing(cueId);
    try {
      await fetch(`${BACKEND_URL}/api/admin/founder-cues/${cueId}/dismiss`, { method: "POST", credentials: "include" });
      setCues(prev => prev.filter(c => c.id !== cueId));
    } catch {}
    setDismissing(null);
  };

  const handleAction = (cue) => {
    if (cue.action_tab) setTab(cue.action_tab);
  };

  const priorityStyle = {
    critical: { border: "1px solid rgba(255,82,82,0.35)", background: "linear-gradient(135deg, rgba(255,82,82,0.10), rgba(255,82,82,0.03))", color: "#FF5252" },
    high: { border: "1px solid rgba(255,179,71,0.35)", background: "linear-gradient(135deg, rgba(255,179,71,0.10), rgba(255,179,71,0.03))", color: "#FFB347" },
    normal: { border: "1px solid rgba(0,255,255,0.20)", background: "linear-gradient(135deg, rgba(0,255,255,0.06), rgba(0,255,255,0.02))", color: "#00FFFF" },
    info: { border: "1px solid rgba(167,139,250,0.25)", background: "linear-gradient(135deg, rgba(167,139,250,0.08), rgba(167,139,250,0.02))", color: "#A78BFA" }
  };

  if (loading) return null;
  if (cues.length === 0) {
    return (
      <div data-testid="founder-cue-empty" style={{
        padding: "14px 18px",
        borderRadius: 12,
        border: "1px solid rgba(0,220,120,0.20)",
        background: "linear-gradient(135deg, rgba(0,220,120,0.06), rgba(0,220,120,0.02))",
        marginBottom: 20,
        display: "flex",
        alignItems: "center",
        gap: 10
      }}>
        <i className="fas fa-circle-check" style={{ color: "#00DC78", fontSize: 16 }}></i>
        <strong style={{ color: "#00DC78", fontSize: 14 }}>You're clear.</strong>
        <span style={{ color: "#ADBED1", fontSize: 13 }}>No actions queued right now. The system will ping you when something needs you.</span>
      </div>
    );
  }

  return (
    <div data-testid="founder-cue-widget" style={{ marginBottom: 22 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 12, marginBottom: 12 }}>
        <h3 style={{ margin: 0, color: "#fff", fontSize: 16, letterSpacing: "0.01em" }}>
          <i className="fas fa-bell" style={{ marginRight: 8, color: "#FFB347" }}></i>
          Action Required
          <span style={{ marginLeft: 10, fontSize: 11, padding: "3px 9px", borderRadius: 10, background: "rgba(255,179,71,0.18)", color: "#FFB347", fontWeight: 700 }}>{cues.length}</span>
        </h3>
        <span style={{ color: "#7E92A8", fontSize: 12 }}>What needs you today · {dashboardRole === "ceo" ? "CEO queue" : dashboardRole === "coo" ? "COO queue" : "Founder queue"}</span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {cues.map(cue => {
          const style = priorityStyle[cue.priority] || priorityStyle.normal;
          return (
            <div key={cue.id} data-testid={`cue-${cue.id}`} style={{
              padding: "14px 16px",
              borderRadius: 10,
              ...style,
              background: style.background,
              border: style.border,
              display: "flex",
              alignItems: "center",
              gap: 12,
              flexWrap: "wrap"
            }}>
              <div style={{ flex: 1, minWidth: 240 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                  <i className={`fas ${cue.icon || "fa-circle-exclamation"}`} style={{ color: style.color, fontSize: 14 }}></i>
                  <strong style={{ color: "#fff", fontSize: 14 }}>{cue.title}</strong>
                  {cue.badge && <span style={{ fontSize: 10, padding: "2px 7px", borderRadius: 8, background: `${style.color}22`, color: style.color, fontWeight: 700, letterSpacing: "0.04em" }}>{cue.badge}</span>}
                </div>
                <p style={{ margin: 0, color: "#ADBED1", fontSize: 13, lineHeight: 1.4 }}>{cue.message}</p>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                {cue.action_label && (
                  <button
                    onClick={() => handleAction(cue)}
                    data-testid={`cue-action-${cue.id}`}
                    style={{
                      padding: "8px 14px",
                      borderRadius: 8,
                      border: `1px solid ${style.color}66`,
                      background: `${style.color}22`,
                      color: style.color,
                      fontSize: 13,
                      fontWeight: 700,
                      cursor: "pointer",
                      whiteSpace: "nowrap"
                    }}
                  >
                    {cue.action_label} →
                  </button>
                )}
                {cue.dismissable !== false && (
                  <button
                    onClick={() => dismissCue(cue.id)}
                    disabled={dismissing === cue.id}
                    data-testid={`cue-dismiss-${cue.id}`}
                    title="Dismiss this cue"
                    style={{
                      padding: "8px 10px",
                      borderRadius: 8,
                      border: "1px solid rgba(255,255,255,0.10)",
                      background: "transparent",
                      color: "#7E92A8",
                      fontSize: 12,
                      cursor: "pointer"
                    }}
                  >
                    {dismissing === cue.id ? "..." : "✕"}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};



const HomeTab = ({ setTab, dashboardRole }) => {
  const [stats, setStats] = useState({ projects: 0, activeProjects: 0, clients: 0, tasks: 0, overdueTasks: 0, crewMembers: 0 });
  const [launchPct, setLaunchPct] = useState(0);
  const [recentActivity, setRecentActivity] = useState([]);
  const [showOnboard, setShowOnboard] = useState(false);
  const [pipeline, setPipeline] = useState({});
  const [followUps, setFollowUps] = useState([]);
  const [sendingFollowUp, setSendingFollowUp] = useState(null);
  const [openTriggerId, setOpenTriggerId] = useState(null);
  const [showManual, setShowManual] = useState(false);

  // Listen for cross-component triggers (e.g., the dashboard search bar
  // selecting "Onboard New Client" while already on the Home tab).
  useEffect(() => {
    const openOnboard = () => setShowOnboard(true);
    const openManual = () => setShowManual(true);
    window.addEventListener("home:open-onboard", openOnboard);
    window.addEventListener("manual:open", openManual);
    return () => {
      window.removeEventListener("home:open-onboard", openOnboard);
      window.removeEventListener("manual:open", openManual);
    };
  }, []);

  const PIPELINE_STAGES = [
    { key: "Submitted", label: "Lead", color: "#888" },
    { key: "Assessed", label: "Walk-Through", color: "#FFA500" },
    { key: "Proposal Sent", label: "Proposal", color: "#4FC3F7" },
    { key: "In Progress", label: "Active", color: "#00FFFF" },
    { key: "Complete", label: "Complete", color: "#4CAF50" },
  ];

  const sendFollowUp = async (projectId) => {
    setSendingFollowUp(projectId);
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/follow-ups/${projectId}/send`, {
        method: "POST", credentials: "include",
      });
      if (!r.ok) {
        let msg = `Server returned ${r.status}. Follow-up not sent.`;
        try { const d = await r.json(); if (d.detail) msg = d.detail; } catch { /* fall through with default msg */ }
        toast.error("Couldn't send follow-up", { description: msg });
      } else {
        // Only remove from the list on confirmed success
        setFollowUps(prev => prev.filter(f => f.project_id !== projectId));
        toast.success("Follow-up sent");
      }
    } catch (e) {
      toast.error("Network error", { description: String(e.message || e) });
    }
    setSendingFollowUp(null);
  };

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [projectsR, clientsR, tasksR, launchR, followUpsR] = await Promise.all([
          fetch(`${BACKEND_URL}/api/admin/projects`, { credentials: "include" }),
          fetch(`${BACKEND_URL}/api/admin/clients`, { credentials: "include" }),
          fetch(`${BACKEND_URL}/api/admin/tasks`, { credentials: "include" }),
          fetch(`${BACKEND_URL}/api/admin/launch-checklist`, { credentials: "include" }),
          fetch(`${BACKEND_URL}/api/admin/follow-ups`, { credentials: "include" }),
        ]);
        const projects = projectsR.ok ? await projectsR.json() : [];
        const clients = clientsR.ok ? await clientsR.json() : [];
        const tasks = tasksR.ok ? await tasksR.json() : [];
        const launch = launchR.ok ? await launchR.json() : { items: {} };
        const fups = followUpsR.ok ? await followUpsR.json() : [];

        const active = Array.isArray(projects) ? projects.filter(p => ["In Progress", "Assessed"].includes(p.status)).length : 0;
        const crew = Array.isArray(clients) ? clients.filter(c => c.role === "crew").length : 0;
        const clientCount = Array.isArray(clients) ? clients.filter(c => c.role === "client").length : 0;
        const overdue = Array.isArray(tasks) ? tasks.filter(t => t.status !== "done" && t.due_date && new Date(t.due_date) < new Date()).length : 0;
        const openTasks = Array.isArray(tasks) ? tasks.filter(t => t.status !== "done").length : 0;

        const totalLaunchItems = LAUNCH_CATEGORIES.reduce((s, c) => s + c.items.length, 0);
        const completedLaunch = Object.values(launch.items || {}).filter(Boolean).length;
        const pct = totalLaunchItems > 0 ? Math.round((completedLaunch / totalLaunchItems) * 100) : 0;

        setStats({ projects: Array.isArray(projects) ? projects.length : 0, activeProjects: active, clients: clientCount, tasks: openTasks, overdueTasks: overdue, crewMembers: crew });
        setLaunchPct(pct);
        if (Array.isArray(fups)) setFollowUps(fups);

        // Build pipeline
        if (Array.isArray(projects)) {
          const pipe = {};
          projects.forEach(p => {
            const status = p.status || "Submitted";
            if (!pipe[status]) pipe[status] = [];
            pipe[status].push(p);
          });
          setPipeline(pipe);
        }

        // Build recent activity from tasks and projects
        const activity = [];
        if (Array.isArray(tasks)) {
          tasks.slice(0, 3).forEach(t => activity.push({ type: "task", text: t.title, status: t.status, date: t.created_at }));
        }
        if (Array.isArray(projects)) {
          projects.slice(0, 3).forEach(p => activity.push({ type: "project", text: `${p.name || p.address}`, status: p.status, date: p.created_at }));
        }
        activity.sort((a, b) => (b.date || "").localeCompare(a.date || ""));
        setRecentActivity(activity.slice(0, 5));
      } catch (e) { console.error("Dashboard stats error:", e); }
    };
    fetchAll();
  }, []);

  const downloadDayOneKit = async () => {
    try {
      const launchR = await fetch(`${BACKEND_URL}/api/admin/launch-checklist`, { credentials: "include" });
      const launch = launchR.ok ? await launchR.json() : { items: {} };
      const items = launch.items || {};

      const doc = new jsPDF();
      doc.setFontSize(18);
      doc.setFont("helvetica", "bold");
      doc.text("MOOVIN FORWARD INC.", 20, 20);
      doc.setFontSize(14);
      doc.text("Day One Kit — Launch Summary", 20, 30);
      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(120);
      doc.text(`Generated: ${new Date().toLocaleDateString()}  |  moovinforward.org  |  (817) 381-8831`, 20, 37);
      doc.setTextColor(0);
      doc.setDrawColor(0, 200, 200);
      doc.line(20, 40, 120, 40);

      let y = 50;
      const totalItems = LAUNCH_CATEGORIES.reduce((s, c) => s + c.items.length, 0);
      const completed = Object.values(items).filter(Boolean).length;
      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.text(`Launch Readiness: ${completed}/${totalItems} complete (${Math.round((completed/totalItems)*100)}%)`, 20, y);
      y += 12;

      LAUNCH_CATEGORIES.forEach(cat => {
        if (y > 265) { doc.addPage(); y = 20; }
        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        doc.text(cat.category.toUpperCase(), 20, y);
        y += 7;
        doc.setFontSize(8.5);
        doc.setFont("helvetica", "normal");
        cat.items.forEach(item => {
          if (y > 275) { doc.addPage(); y = 20; }
          const checked = items[item.id] ? "[X]" : "[ ]";
          doc.text(`${checked}  ${item.label}`, 24, y);
          doc.setTextColor(100);
          doc.text(item.cost, 175, y);
          doc.setTextColor(0);
          y += 5.5;
        });
        y += 4;
      });

      doc.setFontSize(7);
      doc.setTextColor(140);
      doc.text("CONFIDENTIAL — Moovin Forward | moovinforward.org | This is an internal readiness document.", 20, 285);
      doc.save("MF-Day-One-Kit.pdf");
    } catch (e) { console.error("Day One Kit error:", e); }
  };

  const downloadLeaveBehind = () => {
    const doc = new jsPDF();
    // Header
    doc.setFillColor(3, 11, 26); doc.rect(0, 0, 210, 40, "F");
    doc.setFontSize(22); doc.setFont("helvetica", "bold"); doc.setTextColor(0, 255, 255);
    doc.text("MOOVIN FORWARD INC.", 20, 22);
    doc.setFontSize(9); doc.setTextColor(200, 210, 220);
    doc.text("Rapid Stabilization & Occupancy Readiness | DFW Metroplex", 20, 30);
    doc.text("(817) 381-8831 | moovinforward.org", 20, 36);
    // Tagline
    doc.setTextColor(0); doc.setFontSize(13); doc.setFont("helvetica", "bold");
    doc.text("We eliminate vacancy backlog. Your units, market-ready, in days.", 20, 52);
    // The Model
    doc.setFontSize(10); doc.setFont("helvetica", "bold"); doc.setTextColor(0, 150, 150);
    doc.text("THE ROI STABILIZATION MODEL", 20, 64);
    doc.setTextColor(60); doc.setFontSize(9); doc.setFont("helvetica", "normal");
    doc.text("50/50 Activation Capital Model — Materials funded Day 1. No PO delays. No budget surprises.", 20, 71);
    doc.text("Stabilization Fee due only after your walk-through and sign-off. 72-hour settlement.", 20, 77);
    // 3 Tiers
    doc.setFont("helvetica", "bold"); doc.setTextColor(0);
    doc.setFontSize(10); doc.text("SERVICE TIERS", 20, 90);
    doc.setFontSize(9); doc.setFont("helvetica", "normal"); doc.setTextColor(60);
    const tiers = [
      ["SWIFT TURN (48 hrs)", "$950 - $1,300", "Light refresh: paint touch-up, hardware swap, deep clean, LVP spot repair"],
      ["STANDARD (14 days)", "$1,650 - $2,400", "Full repaint, LVP flooring, fixtures, cabinet hardware, deep clean"],
      ["HEAVY RECOVERY (21 days)", "$2,200 - $3,100", "Drywall, full repaint, flooring, fixtures, trash-out, countertops, deep clean"],
    ];
    let ty = 98;
    tiers.forEach(([name, price, desc]) => {
      doc.setFont("helvetica", "bold"); doc.setTextColor(0);
      doc.text(name, 20, ty); doc.text(price, 120, ty);
      doc.setFont("helvetica", "normal"); doc.setTextColor(80);
      doc.text(desc, 20, ty + 5);
      ty += 14;
    });
    // Why Us
    doc.setFont("helvetica", "bold"); doc.setTextColor(0, 150, 150);
    doc.text("WHY MOOVIN FORWARD", 20, ty + 6);
    doc.setTextColor(60); doc.setFont("helvetica", "normal"); doc.setFontSize(8.5);
    const whys = [
      "14-21 day average turn (industry avg: 45 days) — compress vacancy by 60%",
      "NSPIRE / HQS / Section 8 inspection-ready on delivery",
      "Full documentation: before/after photos, line-itemized receipts, NSPIRE checklist",
      "Interchangeable design palettes — market-ready finishes that photograph clean",
      "Durable renter-friendly materials (LVP, modular hardware) = lower long-term maintenance",
      "Cleaning services, trash-outs, and compliance add-ons available",
      "Real-time project tracking via client portal",
    ];
    ty += 13;
    whys.forEach(w => { doc.text(`•  ${w}`, 22, ty); ty += 5; });
    // CTA
    ty += 6;
    doc.setFillColor(0, 200, 200); doc.rect(18, ty - 4, 174, 16, "F");
    doc.setFontSize(11); doc.setFont("helvetica", "bold"); doc.setTextColor(3, 11, 26);
    doc.text("FREE WALK-THROUGH — NO OBLIGATION", 55, ty + 4);
    doc.setFontSize(8); doc.setFont("helvetica", "normal");
    doc.text("Let us walk your vacant unit and show you exactly what we'd do. Call or visit our site.", 35, ty + 10);
    // Footer
    doc.setFontSize(7); doc.setTextColor(140);
    doc.text("Moovin Forward | Shannon Ware, Founder | (817) 381-8831 | moovinforward.org | DFW Metroplex", 20, 288);
    doc.save("MF-Leave-Behind-One-Pager.pdf");
  };

  return (
    <div className="home-tab" data-testid="home-tab">
      {showOnboard && <OnboardingModal onClose={() => setShowOnboard(false)} onSuccess={() => {
        // Refresh stats after onboarding
        setStats(s => ({ ...s, clients: s.clients + 1, projects: s.projects + 1, activeProjects: s.activeProjects + 1 }));
      }} />}

      <div className="home-welcome">
        <h2>{dashboardRole === "ceo" ? "CEO Command Center" : dashboardRole === "coo" ? "COO Operations Center" : "Command Center"}</h2>
        <p>{dashboardRole === "ceo" ? "Pipeline, prospects, and growth tools. Go get the business." : dashboardRole === "coo" ? "Tasks, compliance, payroll, and launch readiness. Keep the engine running." : "Your business at a glance. Start here every day."}</p>
      </div>

      {/* Founder Fire — Shannon's personal motivation, rotates every 45s */}
      <FounderFireBadge />

      {/* Founder Cue System — "The boss with you, not under you" */}
      <FounderCueWidget setTab={setTab} dashboardRole={dashboardRole} />

      {/* Active Field Triggers — issue photos crew flagged */}
      <ActiveTriggersWidget onOpenTrigger={(id) => setOpenTriggerId(id)} />

      {/* Trigger thread modal */}
      {openTriggerId && (
        <div data-testid="trigger-modal" style={{ position: "fixed", inset: 0, background: "rgba(3,11,26,0.92)", backdropFilter: "blur(12px)", zIndex: 1500, display: "flex", alignItems: "center", justifyContent: "center", padding: 16, overflowY: "auto" }}>
          <TriggerThread triggerId={openTriggerId} onClose={() => setOpenTriggerId(null)} />
        </div>
      )}

      {/* Consent Funnel — live stats on the Trade-Vendor Introduction Service */}
      <ConsentDashboardTile mode="compact" setTab={setTab} />

      {/* Field Notes — captured thoughts from on-the-go */}
      <FieldNotesPanel />

      {/* Operator Manual — "Walk Me Through It Like I'm Five" */}
      <div
        data-testid="open-operator-manual"
        onClick={() => setShowManual(true)}
        style={{
          margin: "16px 0 18px",
          padding: "16px 20px",
          background: "linear-gradient(135deg, rgba(0,200,200,0.10) 0%, rgba(167,139,250,0.10) 100%)",
          border: "1.5px solid rgba(0,200,200,0.40)",
          borderRadius: 12,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 14,
          flexWrap: "wrap",
          transition: "transform 0.15s, border-color 0.15s",
        }}
        onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.borderColor = "#00DC78"; }}
        onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.borderColor = "rgba(0,200,200,0.40)"; }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 14, flex: 1, minWidth: 240 }}>
          <div style={{ fontSize: 34 }}>📘</div>
          <div>
            <h3 style={{ color: "#fff", fontSize: 16, fontWeight: 800, margin: 0, letterSpacing: "-0.01em" }}>
              Operator Manual <span style={{ color: "#5AC8D8", fontSize: 11, fontWeight: 700, marginLeft: 6 }}>· Walk Me Through It Like I'm Five</span>
            </h3>
            <p style={{ color: "#ADBED1", fontSize: 12.5, margin: "3px 0 0", lineHeight: 1.45 }}>
              For Shannon &amp; Shaneka. The 5 hubs · Daily Rhythm · FAQs. Master your system one click at a time.
            </p>
          </div>
        </div>
        <div style={{
          background: "linear-gradient(135deg, #00DC78 0%, #5AC8D8 100%)",
          color: "#030B1A",
          padding: "9px 16px",
          borderRadius: 8,
          fontSize: 12,
          fontWeight: 800,
          whiteSpace: "nowrap",
        }}>
          Open →
        </div>
      </div>

      {/* Big Field Mode Launch — one-button access for crew + Shannon */}
      <div
        data-testid="home-field-mode-launch"
        onClick={() => setTab("field-mode")}
        style={{
          margin: "16px 0 22px",
          padding: "18px 22px",
          background: "linear-gradient(135deg, rgba(0,220,120,0.10) 0%, rgba(167,139,250,0.10) 100%)",
          border: "1.5px solid rgba(0,220,120,0.40)",
          borderRadius: 12,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 14,
          flexWrap: "wrap",
          transition: "transform 0.15s, border-color 0.15s",
        }}
        onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.borderColor = "#00DC78"; }}
        onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.borderColor = "rgba(0,220,120,0.40)"; }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 14, flex: 1, minWidth: 240 }}>
          <div style={{ fontSize: 38 }}>🛠</div>
          <div>
            <h3 style={{ color: "#fff", fontSize: 17, fontWeight: 800, margin: 0, letterSpacing: "-0.01em" }}>
              Launch Field Mode <span style={{ color: "#5AC8D8", fontSize: 12, fontWeight: 700, marginLeft: 6 }}>· CAD/CAM</span>
            </h3>
            <p style={{ color: "#ADBED1", fontSize: 13, margin: "3px 0 0", lineHeight: 1.45 }}>
              One-button crew launch — pulls specs, tallies materials, snaps photos, emits the shift PDF.
            </p>
          </div>
        </div>
        <div style={{
          background: "linear-gradient(135deg, #00DC78 0%, #00FFFF 100%)",
          color: "#030B1A",
          padding: "10px 18px",
          borderRadius: 8,
          fontSize: 13,
          fontWeight: 800,
          whiteSpace: "nowrap",
        }}>
          Open →
        </div>
      </div>

      <div className="home-stats-grid" data-testid="home-stats">
        <div className="home-stat-card" onClick={() => setTab("projects")} data-testid="home-stat-projects">
          <span className="home-stat-val">{stats.activeProjects}</span>
          <span className="home-stat-label">Active Projects</span>
          <span className="home-stat-sub">{stats.projects} total</span>
        </div>
        {dashboardRole !== "ceo" && (
          <div className="home-stat-card" onClick={() => setTab("tasks")} data-testid="home-stat-tasks">
            <span className="home-stat-val">{stats.tasks}</span>
            <span className="home-stat-label">Open Tasks</span>
            {stats.overdueTasks > 0 && <span className="home-stat-alert">{stats.overdueTasks} overdue</span>}
          </div>
        )}
        <div className="home-stat-card" onClick={() => setTab("clients")} data-testid="home-stat-clients">
          <span className="home-stat-val">{stats.clients}</span>
          <span className="home-stat-label">Clients</span>
          <span className="home-stat-sub">{stats.crewMembers} crew</span>
        </div>
        {dashboardRole !== "ceo" && (
          <div className="home-stat-card" onClick={() => setTab("launch")} data-testid="home-stat-launch">
            <span className="home-stat-val">{launchPct}%</span>
            <span className="home-stat-label">Launch Ready</span>
            <div className="home-stat-bar"><div className="home-stat-bar-fill" style={{ width: `${launchPct}%` }}></div></div>
          </div>
        )}
      </div>

      {/* Follow-up Alerts - CEO and full admin only */}
      {dashboardRole !== "coo" && followUps.length > 0 && (
        <div className="home-followups" data-testid="home-followups">
          <h3>Follow-up Needed <span className="home-followup-count">{followUps.length}</span></h3>
          <div className="home-followup-list">
            {followUps.slice(0, 5).map(f => (
              <div key={f.project_id} className={`home-followup-item home-followup-${f.urgency}`} data-testid={`followup-${f.project_id}`}>
                <div className="home-followup-info">
                  <span className="home-followup-client">{f.client_name}</span>
                  <span className="home-followup-addr">{f.address}{f.unit_number ? ` #${f.unit_number}` : ""}</span>
                  <span className="home-followup-days">{f.days_stale} days since update</span>
                </div>
                <div className="home-followup-actions">
                  <span className={`home-followup-badge home-followup-badge-${f.urgency}`}>{f.urgency === "critical" ? "Urgent" : f.urgency === "warning" ? "Stale" : "Check in"}</span>
                  <button onClick={() => sendFollowUp(f.project_id)} disabled={sendingFollowUp === f.project_id} className="home-followup-btn" data-testid={`followup-send-${f.project_id}`}>
                    {sendingFollowUp === f.project_id ? "Sending..." : "Send Follow-up"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Client Pipeline - CEO and full admin only */}
      {dashboardRole !== "coo" && (
      <div className="home-pipeline" data-testid="home-pipeline">
        <h3>Client Pipeline</h3>
        <div className="pipeline-board">
          {PIPELINE_STAGES.map(stage => {
            const items = pipeline[stage.key] || [];
            return (
              <div key={stage.key} className="pipeline-col" data-testid={`pipeline-${stage.key.replace(/\s+/g, "-").toLowerCase()}`}>
                <div className="pipeline-col-header" style={{ borderBottomColor: stage.color }}>
                  <span className="pipeline-stage-name">{stage.label}</span>
                  <span className="pipeline-stage-count" style={{ background: items.length > 0 ? stage.color : "rgba(255,255,255,0.08)", color: items.length > 0 ? "#030B1A" : "#A0B3C8" }}>{items.length}</span>
                </div>
                <div className="pipeline-col-body">
                  {items.length === 0 && <div className="pipeline-empty">No projects</div>}
                  {items.map(p => (
                    <div key={p.project_id} className={`pipeline-card ${p.project_type === "receivership" ? "pipeline-card-recv" : ""}`} onClick={() => setTab("projects")} data-testid={`pipeline-card-${p.project_id}`}>
                      <span className="pipeline-card-name">{p.client_name || "Client"}</span>
                      <span className="pipeline-card-addr">{p.address}{p.unit_number ? ` #${p.unit_number}` : ""}</span>
                      {p.project_type === "receivership" && <span className="pipeline-card-recv-badge">Receivership</span>}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      )}

      <div className="home-actions" data-testid="home-quick-actions">
        <h3>Quick Actions</h3>
        <div className="home-action-grid">
          {dashboardRole === "coo" ? (
            <>
              <button onClick={() => setTab("tasks")} className="home-action-btn home-action-special" data-testid="home-action-tasks">Task Board</button>
              <button onClick={() => setTab("payroll")} className="home-action-btn" data-testid="home-action-payroll">Payroll</button>
              <button onClick={() => setTab("contracts")} className="home-action-btn" data-testid="home-action-contracts">Contracts</button>
              <button onClick={() => setTab("launch")} className="home-action-btn" data-testid="home-action-launch">Launch Checklist</button>
            </>
          ) : dashboardRole === "ceo" ? (
            <>
              <button onClick={() => setShowOnboard(true)} className="home-action-btn home-action-special" data-testid="home-action-onboard">Onboard Client</button>
              <button onClick={() => setTab("contracts")} className="home-action-btn" data-testid="home-action-contracts">Prospecting Tools</button>
              <button onClick={() => setTab("operations")} className="home-action-btn" data-testid="home-action-operations">Strategy</button>
              <button onClick={downloadLeaveBehind} className="home-action-btn" data-testid="home-action-leavebehind">Leave-Behind PDF</button>
            </>
          ) : (
            <>
              <button onClick={() => setShowOnboard(true)} className="home-action-btn home-action-special" data-testid="home-action-onboard">Onboard Client</button>
              <button onClick={() => setTab("projects")} className="home-action-btn" data-testid="home-action-projects">Projects</button>
              <button onClick={() => setTab("tasks")} className="home-action-btn" data-testid="home-action-tasks">Add Task</button>
              <button onClick={downloadDayOneKit} className="home-action-btn" data-testid="home-action-dayonekit">Day One Kit PDF</button>
            </>
          )}
        </div>
      </div>

      {recentActivity.length > 0 && (
        <div className="home-activity" data-testid="home-recent-activity">
          <h3>Recent Activity</h3>
          <div className="home-activity-list">
            {recentActivity.map((a, i) => (
              <div key={i} className="home-activity-item">
                <span className={`home-activity-type home-activity-${a.type}`}>{a.type === "task" ? "Task" : "Project"}</span>
                <span className="home-activity-text">{a.text}</span>
                <span className={`home-activity-status home-status-${a.status?.replace(/\s+/g, "-").toLowerCase()}`}>{a.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Shared Board — CEO ↔ COO */}
      {(dashboardRole === "ceo" || dashboardRole === "coo") && (
        <SharedItems dashboardRole={dashboardRole} />
      )}

      {/* Certifications Tracker — visible to both */}
      <CertificationsTracker dashboardRole={dashboardRole} />

      {/* Operator Manual modal — Walk Me Through It Like I'm Five */}
      {showManual && <OperatorManual onClose={() => setShowManual(false)} />}
    </div>
  );
};


export default HomeTab;
